import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GraduationCap } from "lucide-react";
import { usePortal } from "../PortalContext";

export default function ApplicationsTab() {
    const navigate = useNavigate();
    const { applications, applicationsLoading: loading, startApplication, handleComplete: onComplete } = usePortal();
    const onView = (app: { Application_Number: string }) => navigate(`/portal/applications/${app.Application_Number}`);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
                <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">
                    My Applications
                </h3>
                <Button
                    onClick={() => startApplication()}
                    size="sm"
                    className="rounded-xl bg-primary hover:bg-[#002b66] font-bold px-4 h-9 shadow-md"
                >
                    New Application
                </Button>
            </div>

            {loading ? (
                <div className="flex flex-col gap-3">
                    {[1, 2].map((index) => (
                        <Card key={index} className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 h-24 animate-pulse" />
                    ))}
                </div>
            ) : applications.length === 0 ? (
                <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <GraduationCap className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-bold text-slate-700">No applications yet</p>
                    <p className="text-xs text-slate-400">Start your first application to get admitted.</p>
                    <Button
                        onClick={() => startApplication()}
                        size="sm"
                        className="rounded-xl bg-primary hover:bg-[#002b66] font-bold px-4 h-9 mt-1"
                    >
                        Start Application
                    </Button>
                </Card>
            ) : (
                <>
                    {/* Mobile: card list */}
                    <div className="flex flex-col gap-3 md:hidden">
                {applications.map((app) => (
                    <Card
                        key={app.Application_Number}
                        className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-3"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                    <GraduationCap className="h-4 w-4 text-slate-400" />
                                </div>
                                <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                                    {app.Application_Number}
                                </span>
                            </div>
                            <Badge
                                variant="outline"
                                className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap shrink-0 ${
                                    app.Action === "Complete"
                                        ? "border-amber-100 text-amber-600 bg-amber-50/50"
                                        : app.Stage === "Evaluation"
                                          ? "border-blue-100 text-blue-600 bg-blue-50/50"
                                          : "border-emerald-100 text-emerald-600 bg-emerald-50/50"
                                }`}
                            >
                                {app.Stage || "—"}
                            </Badge>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-800">{app.Applicant_Name}</p>
                            <p className="text-xs text-slate-500">{app.Applied_Program_Name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span>{app.Stage}</span>
                                {app.Applied_Term && <><span>·</span><span>{app.Applied_Term}</span></>}
                            </div>
                        </div>
                        <Button
                            variant={app.Action === "Complete" ? "default" : "outline"}
                            size="sm"
                            onClick={() => app.Action === "Complete" ? onComplete(app) : onView(app)}
                            className={`w-full h-9 font-bold text-xs rounded-xl ${
                                app.Action === "Complete"
                                    ? "bg-primary hover:bg-[#002b66]"
                                    : "border-slate-200 text-slate-600 hover:text-primary"
                            }`}
                        >
                            {app.Action === "Complete" ? "Complete Application" : "View Details"}
                        </Button>
                    </Card>
                ))}
            </div>

            {/* Desktop: table */}
            <Card className="hidden md:block border border-slate-100 shadow-sm rounded-2xl overflow-hidden bg-white">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                                Application Number
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                                Applicant Name
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">
                                Applied Program
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">
                                Stage
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden xl:table-cell whitespace-nowrap">
                                Applied Term
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                                Application Status
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {applications.map((app) => (
                            <tr
                                key={app.Application_Number}
                                className="group hover:bg-slate-50/30 transition-colors"
                            >
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-primary/5 transition-colors">
                                            <GraduationCap className="h-4 w-4 text-slate-400 group-hover:text-primary" />
                                        </div>
                                        <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                                            {app.Application_Number}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className="text-sm font-medium text-slate-700">
                                        {app.Applicant_Name}
                                    </span>
                                </td>
                                <td className="px-6 py-4 hidden lg:table-cell">
                                    <span className="text-sm font-medium text-slate-700">
                                        {app.Applied_Program_Name}
                                    </span>
                                </td>
                                <td className="px-6 py-4 hidden lg:table-cell">
                                    <span className="text-xs font-medium text-slate-500">
                                        {app.Stage}
                                    </span>
                                </td>
                                <td className="px-6 py-4 hidden xl:table-cell">
                                    <span className="text-xs font-medium text-slate-500">
                                        {app.Applied_Term}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <Badge
                                        variant="outline"
                                        className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap ${
                                            app.Action === "Complete"
                                                ? "border-amber-100 text-amber-600 bg-amber-50/50"
                                                : app.Stage === "Evaluation"
                                                  ? "border-blue-100 text-blue-600 bg-blue-50/50"
                                                  : "border-emerald-100 text-emerald-600 bg-emerald-50/50"
                                        }`}
                                    >
                                        {app.Stage || "—"}
                                    </Badge>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <Button
                                        variant={app.Action === "Complete" ? "default" : "outline"}
                                        size="sm"
                                        onClick={() =>
                                            app.Action === "Complete" ? onComplete(app) : onView(app)
                                        }
                                        className={`h-8 font-bold text-xs rounded-lg px-3 ${
                                            app.Action === "Complete"
                                                ? "bg-primary hover:bg-[#002b66]"
                                                : "border-slate-200 text-slate-600 hover:text-primary"
                                        }`}
                                    >
                                        {app.Action === "Complete" ? "Complete" : "View"}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
                </>
            )}
        </div>
    );
}
